/**
 * `<InterviewSlotOverlay>` — top-level overlay for the build-game
 * interview engine v1 push channels (TDD §6.2 + §8.1 + §17.2).
 *
 * Subscribes to four push channels:
 *
 *   - `interview:show-slot`     → mounts `<SlotCard>`
 *   - `interview:show-review`   → mounts `<ReviewCard>` (stub)
 *   - `interview:show-takeover` → mounts `<TakeoverCard>` (stub)
 *   - `interview:complete`      → dismisses the active card
 *
 * Per TDD §17.2, ALL subscriptions are gated **at registration time**
 * on `useFeatureFlags().interviewEngineV1`. With the flag OFF, no
 * listener is registered and incoming pushes drop on the floor inside
 * Electron's IPC layer — no "phantom card" UI is ever rendered. The
 * useEffect re-runs when the flag flips so a hot-reload-during-dev
 * scenario picks up the change without a restart (production semantics
 * still require a restart per the comment at the top of
 * `useFeatureFlags`).
 *
 * Cross-game guard: the overlay drops pushes whose `push.gameId !==`
 * the prop `gameId`. This mirrors the bulk-interview subscription's
 * convention in `Workspace.tsx` so a renderer with multiple workspace
 * tabs in flight doesn't cross-pollinate cards.
 *
 * Answer dispatch: each card's `onSubmit` / `onSkip` / `onCancel` /
 * `onConfirm` invokes the matching `interview:answer-*` IPC. The
 * main-side handler (PR D) returns `{ ok: false, reason: 'feature-
 * disabled' }` when the flag is OFF; the overlay treats that as a
 * silent no-op (`console.debug` once, drop the response).
 */

import { useEffect, useRef, useState } from 'react';
import type {
  PresentContext,
  PresentResult,
  ReviewContext,
  ReviewResult,
  SlotAnswerValue,
  SlotDefinition,
  TakeoverContext,
  TakeoverDecision,
} from '@volley/crucible/interview/types.js';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { SlotCard } from './SlotCard';

// ---------------------------------------------------------------------------
// View mode (issue #350 — re-wired express interview)
// ---------------------------------------------------------------------------
//
// `paginated` (default): one slot card at a time — the original UX
// since PR G (hello-weekend v0.14.0) replaced Phase A's chat-driven
// interview with the FSM engine.
//
// `stacked`: a multi-row form where every previously-answered slot is
// rendered as a read-only history row above the active slot's editable
// card. The user toggles between modes via the "Show all" button in
// the overlay header. Triggered pre-flight by the empty-state /
// composer "speed-run" buttons (`onStartExpressInterview` in
// Workspace.tsx) which set the initial `viewMode` to `stacked`.
//
// **Engine constraint**: the FSM engine pushes ONE slot at a time
// (`interview:show-slot` → `interview:answer-slot` round-trip per slot)
// because branch resolution depends on prior answers. Stacked mode is
// therefore "accumulating history with active row" rather than the
// v0.13 BulkInterviewCard "answer all upfront" form. The history rows
// give power users continuous visual context of what they've answered;
// the active row stays interactive and submits drive the engine
// forward in the same per-slot cadence the paginated mode uses.

export type InterviewViewMode = 'paginated' | 'stacked';

/** Snapshot of a slot we've already answered in this overlay session.
 *  Stored in renderer-only state so a paginated → stacked flip surfaces
 *  the answers as history rows. The engine's authoritative state file
 *  is the source of truth for resume / dispatch. */
export interface AnsweredSlotSnapshot {
  slot: SlotDefinition;
  context: PresentContext;
  result: PresentResult;
}

// ---------------------------------------------------------------------------
// Local UI state
// ---------------------------------------------------------------------------

type ActiveCard =
  | { kind: 'none' }
  | { kind: 'slot'; slot: SlotDefinition; context: PresentContext }
  | { kind: 'review'; context: ReviewContext }
  | { kind: 'takeover'; context: TakeoverContext };

export interface InterviewSlotOverlayProps {
  gameId: string;
  /**
   * Optional injection seam for the IPC bridge. Tests pass a stub;
   * production reads from `window.crucible`. Keeps the overlay's
   * subscription / invoke calls testable without a fragile
   * jsdom-style `window` mock.
   */
  bridge?: InterviewBridge;
  /**
   * Initial render mode. `paginated` (default) shows one slot card at
   * a time. `stacked` shows answered rows as a history pane above the
   * active card. The user can flip between modes mid-interview via
   * the "Show all" / "Show one" header button. Issue #350.
   *
   * Pre-flight express UX: Workspace.tsx's empty-state speed-run
   * button + persistent icon toggle pass `'stacked'` here so the
   * overlay mounts in the express layout from the very first slot.
   */
  initialViewMode?: InterviewViewMode;
  onRendererReady?: () => void;
}

/** Minimal IPC surface the overlay uses. Mirrors `window.crucible`. */
export interface InterviewBridge {
  on<K extends keyof InterviewBridgePushMap>(
    channel: K,
    callback: (data: InterviewBridgePushMap[K]) => void,
  ): () => void;
  invoke<K extends keyof InterviewBridgeInvokeMap>(
    channel: K,
    args: InterviewBridgeInvokeMap[K][0],
  ): Promise<InterviewBridgeInvokeMap[K][1]>;
}

interface InterviewBridgePushMap {
  'interview:show-slot': { gameId: string; slot: SlotDefinition; context: PresentContext };
  'interview:show-review': { gameId: string; context: ReviewContext };
  'interview:show-takeover': { gameId: string; context: TakeoverContext };
  'interview:complete': { gameId: string; summary: unknown };
  /** Issue #464 — yolo blocked by required+non-inferable slots. */
  'interview:yolo-blocked': {
    gameId: string;
    blockers: readonly { slotId: string; agentName: string; text: string }[];
  };
}

interface InterviewBridgeInvokeMap {
  'interview:answer-slot': [
    { gameId: string; slotId: string; result: PresentResult },
    { ok: boolean; reason?: string },
  ];
  'interview:answer-review': [
    { gameId: string; result: ReviewResult },
    { ok: boolean; reason?: string },
  ];
  'interview:answer-takeover': [
    { gameId: string; decision: TakeoverDecision },
    { ok: boolean; reason?: string },
  ];
  /** Codex Round-1 fix on PR #301 — renderer-readiness ack. The
   *  overlay fires this once on mount (only when the engine flag is
   *  ON). Until main receives it, `agent-adapter` short-circuits the
   *  engine kickoff to the legacy SKILL path. Prevents lost-push
   *  hangs where the engine runs before any subscriber is installed. */
  'interview:ready': [Record<string, never>, { ok: boolean; reason?: string }];
  /** crucible#2221 — GUI escape hatch. Fired when the user clicks
   *  "This game is already built — skip the interview" in the slot
   *  overlay footer. Main writes a terminal-built interview state +
   *  stamps `crucible.json.buildStatus`, so the game routes to preview
   *  and the fix survives future clones. Covers PRE-SIGNAL games (a
   *  manual clone of a shipped game) that the observe-and-stamp path
   *  never had a chance to stamp. */
  'build-game:skip-interview': [{ gameId: string }, { ok: boolean }];
}

/**
 * Extract the audioPath from a `prefilled` SlotAnswerValue when the
 * engine threaded a `FreeTextWithVoice` through the IPC frame
 * (TDD §6.2). Returns `undefined` for any other shape — plain
 * string transcripts, single-select strings, multi-pick arrays,
 * sliders, etc. — none of which carry voice metadata.
 *
 * Codex Round-1 fix on PR #302: previously the renderer dropped
 * audioPath even when it WAS present in `context.prefilled`,
 * because the SlotCard's separate `audioPath` prop had no source.
 * The transcript was reconstructed but the audio affordance never
 * rendered. Pulling it out here threads the path through unchanged.
 */
export function extractAudioPath(prefilled: SlotAnswerValue | undefined): string | undefined {
  if (
    prefilled !== undefined &&
    !Array.isArray(prefilled) &&
    typeof prefilled === 'object' &&
    'kind' in prefilled &&
    prefilled.kind === 'voice'
  ) {
    return prefilled.audioPath;
  }
  return undefined;
}

/** Resolve the bridge for the running environment. Production reads
 *  `window.crucible`; tests inject. Returns `null` when neither is
 *  available (SSR, vitest's no-jsdom mode), and the overlay then
 *  short-circuits to render-nothing. */
function resolveBridge(injected: InterviewBridge | undefined): InterviewBridge | null {
  if (injected) return injected;
  if (typeof window !== 'undefined' && window.crucible) {
    return window.crucible as unknown as InterviewBridge;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Pure helpers — extracted so the §17.2 flag-gate semantics can be
// tested without a DOM (the renderer test surface is SSR-only).
// ---------------------------------------------------------------------------

export type ActiveCardSetter = (next: ActiveCard) => void;

/**
 * Issue #464 — single yolo-blocker entry surfaced when the speed-run
 * button fires `interview:yolo-remaining` and main detects one or
 * more required+non-inferable slots without a usable default. The
 * overlay renders a flat "these still need a real answer" list
 * above the active slot card so the user can see what's left.
 */
export interface YoloBlocker {
  slotId: string;
  agentName: string;
  text: string;
}
export type YoloBlockerSetter = (next: readonly YoloBlocker[]) => void;

/**
 * Register the four push channel listeners on the bridge, gated by
 * the §17.2 contract: with the flag OFF, ZERO listeners are attached.
 * Returns an unsubscribe function that removes every registered
 * listener (callable on flag flip / unmount). The cross-game guard
 * is applied here so a test bridge can verify the drop behaviour.
 *
 * Exported for tests; the component's `useEffect` is the production
 * caller. Keep the signature pure (no React imports) — the test
 * surface is the wire-shape, not the React lifecycle.
 */
export function registerInterviewSubscriptions(args: {
  bridge: InterviewBridge;
  gameId: string;
  flagsLoaded: boolean;
  interviewEngineV1: boolean;
  setActive: ActiveCardSetter;
  /** Issue #464 — optional setter for the yolo-blocker list. The
   *  overlay's `useEffect` provides it; tests that drive
   *  `registerInterviewSubscriptions` directly can omit it (the
   *  unsubscribe function still works). */
  setYoloBlockers?: YoloBlockerSetter;
}): () => void {
  const { bridge, gameId, flagsLoaded, interviewEngineV1, setActive, setYoloBlockers } = args;

  if (!flagsLoaded || !interviewEngineV1) {
    // §17.2 — no listener is registered with the flag OFF. Pushes
    // drop on the floor in Electron's IPC layer.
    return () => {};
  }

  const unsubSlot = bridge.on('interview:show-slot', (push) => {
    if (push.gameId !== gameId) return;
    setActive({ kind: 'slot', slot: push.slot, context: push.context });
  });
  const unsubReview = bridge.on('interview:show-review', (push) => {
    if (push.gameId !== gameId) return;
    setActive({ kind: 'review', context: push.context });
  });
  const unsubTakeover = bridge.on('interview:show-takeover', (push) => {
    if (push.gameId !== gameId) return;
    setActive({ kind: 'takeover', context: push.context });
  });
  const unsubComplete = bridge.on('interview:complete', (push) => {
    if (push.gameId !== gameId) return;
    setActive({ kind: 'none' });
    if (setYoloBlockers) setYoloBlockers([]);
  });
  // Issue #464 — yolo blocker push. Main fires this when the speed-
  // run IPC encounters required+non-inferable slots that have no
  // usable default. The overlay surfaces the blocker list above the
  // active slot card so the user knows what's still owed.
  const unsubYoloBlocked = bridge.on('interview:yolo-blocked', (push) => {
    if (push.gameId !== gameId) return;
    if (setYoloBlockers) setYoloBlockers([...push.blockers]);
  });

  return () => {
    unsubSlot();
    unsubReview();
    unsubTakeover();
    unsubComplete();
    unsubYoloBlocked();
  };
}

/**
 * Fire `interview:ready` once subscriptions are installed and notify
 * the workspace when main acknowledges. Returns a teardown that
 * suppresses `onRendererReady` if the effect cleans up before the
 * promise settles (gameId change, flag flip, unmount).
 *
 * Exported for tests; the component's `useEffect` is the production
 * caller.
 */
export function invokeInterviewReadyHandshake(args: {
  bridge: InterviewBridge;
  onRendererReady?: () => void;
}): () => void {
  const { bridge, onRendererReady } = args;
  let cancelled = false;

  void bridge
    .invoke('interview:ready', {})
    .then(() => {
      if (!cancelled) {
        onRendererReady?.();
      }
    })
    .catch((err: unknown) => {
      if (cancelled) return;
      // Codex Round-2 fix on PR #302: don't swallow blindly. When
      // the IPC channel is missing entirely (PR #301 not yet
      // merged) or main hits an unexpected error, the engine
      // kickoff stays silently gated — `/build-game` falls
      // through to legacy SKILL with no obvious signal that the
      // engine isn't running. Surface to console so a curious
      // dev opening DevTools sees the cause; don't surface to
      // the user because the legacy fallback IS the right
      // failure mode (no user-visible regression).
      console.warn(
        '[crucible-interview] interview:ready failed; engine kickoff will stay gated and /build-game will use legacy SKILL path',
        err,
      );
    });

  return () => {
    cancelled = true;
  };
}

/** Re-export the active-card union for tests. */
export type { ActiveCard };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterviewSlotOverlay({
  gameId,
  bridge: injectedBridge,
  initialViewMode = 'paginated',
  onRendererReady,
}: InterviewSlotOverlayProps): React.JSX.Element | null {
  const flags = useFeatureFlags();
  const [active, setActive] = useState<ActiveCard>({ kind: 'none' });
  const [viewMode, setViewMode] = useState<InterviewViewMode>(initialViewMode);
  const [answered, setAnswered] = useState<readonly AnsweredSlotSnapshot[]>([]);
  /** Issue #464 — yolo blockers surfaced by `interview:yolo-blocked`. */
  const [yoloBlockers, setYoloBlockers] = useState<readonly YoloBlocker[]>([]);

  // Keep the latest workspace callback without putting it in the
  // subscription/ready effect deps — an unstable parent reference
  // (inline arrow) would otherwise tear down listeners and re-fire
  // `interview:ready` on every render, breaking the once-per-mount
  // handshake contract.
  const onRendererReadyRef = useRef(onRendererReady);
  onRendererReadyRef.current = onRendererReady;

  // Reset accumulated history whenever the gameId changes — switching
  // workspaces should not surface a previous game's answers in the new
  // game's stacked pane. The engine's state file owns persistence; the
  // overlay's history is presentation-only and per-mount.
  useEffect(() => {
    setAnswered([]);
    setYoloBlockers([]);
  }, [gameId]);

  // Pre-flight express signal arriving AFTER mount (rare — the prop is
  // typically stable from Workspace) flips the live mode without
  // wiping history. The dependency is a primitive so the effect only
  // fires on real changes.
  useEffect(() => {
    setViewMode(initialViewMode);
  }, [initialViewMode]);

  useEffect(() => {
    // §17.2 contract — only register subscriptions when the flag has
    // loaded AND is ON. The pure helper enforces the gate; with the
    // flag OFF it returns an empty unsubscribe and registers nothing.
    const bridge = resolveBridge(injectedBridge);
    if (!bridge) return;
    const unsub = registerInterviewSubscriptions({
      bridge,
      gameId,
      flagsLoaded: flags.loaded,
      interviewEngineV1: flags.interviewEngineV1,
      setActive,
      setYoloBlockers,
    });
    // Codex Round-1 fix on PR #301 — fire `interview:ready` ONCE the
    // listeners are installed. Main flips its renderer-readiness
    // latch and unlocks the engine kickoff path. Without this,
    // `agent-adapter`'s kickoff would skip the engine and fall
    // through to the legacy SKILL path even when the flag is on.
    //
    // Only fire when subscriptions are actually installed (flag ON
    // + flag loaded). The main-side handler is also flag-gated and
    // will return `feature-disabled` if the flag is OFF — so a
    // theoretical race between flag-flip and this effect is harmless.
    const cancelReady =
      flags.loaded && flags.interviewEngineV1
        ? invokeInterviewReadyHandshake({
            bridge,
            onRendererReady: () => onRendererReadyRef.current?.(),
          })
        : undefined;
    return () => {
      cancelReady?.();
      unsub();
    };
  }, [gameId, flags.loaded, flags.interviewEngineV1, injectedBridge]);

  if (!flags.loaded || !flags.interviewEngineV1) return null;
  if (active.kind === 'none') return null;

  const bridge = resolveBridge(injectedBridge);
  if (!bridge) return null;

  // ── Slot push ────────────────────────────────────────────────────
  if (active.kind === 'slot') {
    const { slot, context } = active;
    const handleSubmit = (result: PresentResult): void => {
      void dispatchInvoke(bridge, 'interview:answer-slot', {
        gameId,
        slotId: slot.id,
        result,
      });
      // Push the answered snapshot into the renderer-only history
      // BEFORE clearing `active` so a paginated → stacked flip
      // immediately surfaces it. Skipped slots are tracked too so the
      // user can see what they intentionally bypassed; the engine
      // records 'user-skipped' authoritatively.
      setAnswered((prev) => [...prev, { slot, context, result }]);
      // Issue #464 — shrink the yolo blocker list as the user works
      // through it. Filtering by slotId means a non-blocker answer
      // is a no-op (most lists are 0-1 entries) and a blocker answer
      // visibly removes the row, signalling progress.
      setYoloBlockers((prev) => prev.filter((b) => b.slotId !== slot.id));
      setActive({ kind: 'none' });
    };
    // Codex Round-1 fix on PR #302: extract audioPath from a
    // FreeTextWithVoice prefill so a resumed voice answer renders
    // its audio affordance (TDD §4.1 + §6.2). The engine threads
    // `slotMeta[id].audioPath` through `context.prefilled` when the
    // existing slot answer was inferred-from-voice; the renderer
    // here destructures it and passes it as a separate prop to
    // <SlotCard>, which the free-text card consumes.
    const audioPath = extractAudioPath(context.prefilled);
    // crucible#2221 — escape hatch. The user is staring at a build
    // interview for a game they know is already built (the classic
    // case: a manual `git clone` of a shipped game whose gitignored
    // `.foundry/` interview state never travelled). One click flips it
    // to the terminal-built state + stamps the durable signal, then the
    // overlay dismisses — main's `game:state-changed` push drives the
    // workspace to re-read and route to preview.
    const handleSkipInterview = (): void => {
      void dispatchInvoke(bridge, 'build-game:skip-interview', { gameId });
      setActive({ kind: 'none' });
    };
    return (
      <div
        className="mb-4"
        data-testid="interview-slot-overlay-mount"
        data-game-id={gameId}
        data-slot-id={slot.id}
        data-view-mode={viewMode}
      >
        <ViewModeHeader
          mode={viewMode}
          answeredCount={answered.length}
          onToggle={() => setViewMode((prev) => (prev === 'stacked' ? 'paginated' : 'stacked'))}
        />
        {viewMode === 'stacked' && answered.length > 0 && (
          <AnsweredHistoryPane gameId={gameId} answered={answered} />
        )}
        {yoloBlockers.length > 0 && <YoloBlockerPane gameId={gameId} blockers={yoloBlockers} />}
        <SlotCard
          gameId={gameId}
          slot={slot}
          context={context}
          audioPath={audioPath}
          onSubmit={handleSubmit}
          onSkip={handleSubmit}
          newUi={flags.newUi}
        />
        <SkipInterviewFooter gameId={gameId} onSkip={handleSkipInterview} />
      </div>
    );
  }

  // ── Review push ─────────────────────────────────────────────────
  if (active.kind === 'review') {
    const { context } = active;
    const handleConfirm = (): void => {
      void dispatchInvoke(bridge, 'interview:answer-review', {
        gameId,
        result: { kind: 'confirmed' },
      });
      setActive({ kind: 'none' });
    };
    const handleEdit = (slotId: string): void => {
      void dispatchInvoke(bridge, 'interview:answer-review', {
        gameId,
        result: { kind: 'edit', slotId },
      });
      setActive({ kind: 'none' });
    };
    return (
      <ReviewCard gameId={gameId} context={context} onConfirm={handleConfirm} onEdit={handleEdit} />
    );
  }

  // ── Takeover push ───────────────────────────────────────────────
  const { context } = active;
  const handleDecision = (decision: TakeoverDecision): void => {
    void dispatchInvoke(bridge, 'interview:answer-takeover', { gameId, decision });
    setActive({ kind: 'none' });
  };
  return <TakeoverCard gameId={gameId} context={context} onDecision={handleDecision} />;
}

// ---------------------------------------------------------------------------
// Invoke wrapper — single source of truth for the §17.2 silent-no-op
// handling. Logs once at debug level when the flag is OFF.
// ---------------------------------------------------------------------------

async function dispatchInvoke<K extends keyof InterviewBridgeInvokeMap>(
  bridge: InterviewBridge,
  channel: K,
  payload: InterviewBridgeInvokeMap[K][0],
): Promise<void> {
  try {
    const response = await bridge.invoke(channel, payload);
    // Not every channel response carries `reason` (crucible#2221's
    // `build-game:skip-interview` returns a bare `{ ok }`), so read it
    // defensively rather than off the union.
    const reason = (response as { reason?: string }).reason;
    if (!response.ok && reason === 'feature-disabled') {
      // Flag flipped off mid-flight — drop. TDD §17.2 guarantees this
      // is silent on the user surface; log once for debugging.
      // eslint-disable-next-line no-console
      console.debug(`[interview-overlay] ${channel} returned feature-disabled; dropping.`);
    }
  } catch (err) {
    console.error(`[interview-overlay] ${channel} invoke failed`, err);
  }
}

// ---------------------------------------------------------------------------
// Stub: <ReviewCard>
//
// Minimal v1 — lists every answered slot with an Edit button per row
// plus a single Confirm button at the foot. Visual treatment piggy-
// backs on `<ReviewAnswersModal>`'s palette so the user reads them as
// the same surface family.
// ---------------------------------------------------------------------------

interface ReviewCardProps {
  gameId: string;
  context: ReviewContext;
  onConfirm: () => void;
  onEdit: (slotId: string) => void;
}

export function ReviewCard({
  gameId,
  context,
  onConfirm,
  onEdit,
}: ReviewCardProps): React.JSX.Element {
  return (
    <div
      className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-3"
      data-testid="interview-review-card"
      data-game-id={gameId}
    >
      <p className="mb-2 text-sm font-medium text-white/90">
        Review your answers before we dispatch
      </p>
      <ul className="space-y-1.5">
        {context.slots.map((slot) => {
          const ans = context.answers[slot.id];
          return (
            <li
              key={slot.id}
              data-testid={`interview-review-card-row-${slot.id}`}
              className="flex items-start gap-2 text-xs"
            >
              <div className="flex-1">
                <p className="text-white/60">{slot.text}</p>
                <p className="mt-0.5 text-white/90">{summariseAnswer(ans)}</p>
              </div>
              <button
                type="button"
                onClick={() => onEdit(slot.id)}
                data-testid={`interview-review-card-edit-${slot.id}`}
                className="rounded border border-white/10 px-2 py-0.5 text-[11px] text-white/70 transition-colors hover:border-white/20 hover:text-white"
              >
                Edit
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex justify-end border-t border-white/10 pt-3">
        <button
          type="button"
          onClick={onConfirm}
          data-testid="interview-review-card-confirm"
          className="rounded-lg bg-emerald-500/80 px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
        >
          Looks right — dispatch
        </button>
      </div>
    </div>
  );
}

function summariseAnswer(answer: ReviewContext['answers'][string] | undefined): string {
  if (!answer || answer.value === undefined) return '(unanswered)';
  const v = answer.value;
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.join(', ');
  // FreeTextWithVoice
  return v.transcript;
}

// ---------------------------------------------------------------------------
// View-mode header + answered-history pane (issue #350)
//
// The header lives at the top of the active slot card surface and
// carries the `Show all` / `Show one` toggle. The answered-history
// pane renders below the header (still above the active SlotCard) and
// lists every prior answer the user has submitted in this overlay
// session as a read-only summary row.
//
// Both are exported so the SSR-pin tests in
// `InterviewSlotOverlay.test.tsx` can render them in isolation
// without a full DOM mount.
// ---------------------------------------------------------------------------

interface ViewModeHeaderProps {
  mode: InterviewViewMode;
  answeredCount: number;
  onToggle: () => void;
}

export function ViewModeHeader({
  mode,
  answeredCount,
  onToggle,
}: ViewModeHeaderProps): React.JSX.Element {
  // Copy is symmetric: in paginated mode the button widens the layout;
  // in stacked mode it narrows it back to one card.
  const label = mode === 'stacked' ? 'One at a time' : 'All questions';
  const ariaLabel =
    mode === 'stacked' ? 'Switch to one-card-at-a-time view' : 'Switch to all-cards-stacked view';
  return (
    <div
      className="mb-2 flex items-center justify-between gap-2"
      data-testid="interview-view-mode-header"
      data-mode={mode}
    >
      <span className="text-[11px] uppercase tracking-wide text-white/40">
        {mode === 'stacked' ? (
          <>
            All questions
            {answeredCount > 0 && (
              <span className="ml-1 text-white/30">· {answeredCount} answered</span>
            )}
          </>
        ) : (
          'Interview'
        )}
      </span>
      <button
        type="button"
        onClick={onToggle}
        data-testid="interview-view-mode-toggle"
        aria-label={ariaLabel}
        className="rounded border border-white/10 px-2 py-0.5 text-[11px] text-white/70 transition-colors hover:border-white/20 hover:text-white"
      >
        {label}
      </button>
    </div>
  );
}

interface AnsweredHistoryPaneProps {
  gameId: string;
  answered: readonly AnsweredSlotSnapshot[];
}

export function AnsweredHistoryPane({
  gameId,
  answered,
}: AnsweredHistoryPaneProps): React.JSX.Element {
  return (
    <div
      className="mb-3 rounded-md border border-white/10 bg-black/10 px-3 py-2"
      data-testid="interview-answered-history"
      data-game-id={gameId}
      data-count={answered.length}
    >
      <p className="mb-1.5 text-[10px] uppercase tracking-wide text-white/30">Already answered</p>
      <ul className="space-y-1">
        {answered.map((entry, idx) => (
          <li
            key={`${entry.slot.id}-${idx}`}
            className="flex items-start gap-2 text-xs"
            data-testid={`interview-answered-history-row-${entry.slot.id}`}
          >
            <span className="text-white/30 tabular-nums">{entry.context.index}.</span>
            <div className="flex-1 min-w-0">
              <p className="truncate text-white/50">{entry.slot.text}</p>
              <p className="truncate text-white/80">{summariseSlotResult(entry.result)}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Issue #464 — yolo-blocker pane. Surfaces when the speed-run IPC
 * (`interview:yolo-remaining`) returns blockers — slots that are
 * required AND non-inferable AND have no usable default, so the
 * engine can't drain to dispatch without the user answering them.
 *
 * Rendered above the active slot card; rows shrink as the user
 * answers each one (the overlay's `handleSubmit` filters the just-
 * answered slotId out of the list). Visual treatment uses amber to
 * distinguish from the emerald-toned answered-history pane —
 * "these still need a real answer" vs "what you already did".
 */
interface YoloBlockerPaneProps {
  gameId: string;
  blockers: readonly YoloBlocker[];
}

export function YoloBlockerPane({ gameId, blockers }: YoloBlockerPaneProps): React.JSX.Element {
  return (
    <div
      className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2"
      data-testid="interview-yolo-blockers"
      data-game-id={gameId}
      data-count={blockers.length}
    >
      <p className="mb-1.5 text-[10px] uppercase tracking-wide text-amber-300/80">
        Phase A nearly done — {blockers.length} slot{blockers.length === 1 ? '' : 's'} still need a
        real answer
      </p>
      <ul className="space-y-1">
        {blockers.map((b) => (
          <li
            key={b.slotId}
            className="flex items-start gap-2 text-xs"
            data-testid={`interview-yolo-blocker-row-${b.slotId}`}
          >
            <span className="text-amber-300/70">·</span>
            <div className="flex-1 min-w-0">
              <p className="truncate text-white/80">{b.text}</p>
              <p className="truncate text-[10px] text-white/40">{b.agentName}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * crucible#2221 — "This game is already built — skip the interview"
 * footer. Rendered beneath the active slot card so a user who knows the
 * game is built (the manual-clone-of-a-shipped-game case) can self-
 * rescue without hand-editing gitignored `.foundry/` JSON. Deliberately
 * low-key (muted text + a small bordered button consistent with the
 * overlay's other secondary actions) so it doesn't compete with the
 * primary "answer the question" affordance.
 */
interface SkipInterviewFooterProps {
  gameId: string;
  onSkip: () => void;
}

export function SkipInterviewFooter({
  gameId,
  onSkip,
}: SkipInterviewFooterProps): React.JSX.Element {
  return (
    <div
      className="mt-2 flex items-center justify-between gap-2 border-t border-white/10 pt-2"
      data-testid="interview-skip-interview-footer"
      data-game-id={gameId}
    >
      <span className="text-[11px] text-white/40">Cloned a game that's already built?</span>
      <button
        type="button"
        onClick={onSkip}
        data-testid="interview-skip-interview-button"
        aria-label="Skip the interview — this game is already built"
        className="rounded border border-white/10 px-2 py-0.5 text-[11px] text-white/70 transition-colors hover:border-white/20 hover:text-white"
      >
        Already built — skip the interview
      </button>
    </div>
  );
}

/** Render a `PresentResult` to a one-line human summary for the
 *  history pane. Mirrors `summariseAnswer`'s shape from the review
 *  card but reads off the present-result discriminant rather than the
 *  state-file's `SlotAnswer`. */
export function summariseSlotResult(result: PresentResult): string {
  if (result.kind === 'skipped') return '(skipped)';
  if (result.kind === 'cancelled') return '(cancelled)';
  if (result.kind === 'parser-needs-retry') return '(retrying…)';
  // result.kind === 'answered'
  const v = result.value;
  if (typeof v === 'string') return v.length > 0 ? v : '(empty)';
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.length > 0 ? v.join(', ') : '(none)';
  // FreeTextWithVoice
  return v.transcript.length > 0 ? v.transcript : '(empty)';
}

// ---------------------------------------------------------------------------
// Stub: <TakeoverCard>
//
// Minimal v1 — three buttons (Take over, Wait, Start over). Hides the
// 'take-over' button when `context.isLive` is true (per the
// `InterviewShowTakeoverPush` doc comment in `ipc-channels.ts`).
// ---------------------------------------------------------------------------

interface TakeoverCardProps {
  gameId: string;
  context: TakeoverContext;
  onDecision: (decision: TakeoverDecision) => void;
}

export function TakeoverCard({
  gameId,
  context,
  onDecision,
}: TakeoverCardProps): React.JSX.Element {
  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-3"
      data-testid="interview-takeover-card"
      data-game-id={gameId}
      data-is-live={context.isLive ? 'true' : 'false'}
    >
      <p className="mb-1 text-sm font-medium text-white/90">Another session is in this interview</p>
      <p className="mb-3 text-xs text-white/60">
        Held by <span className="text-white/80">{context.heldBy.holderHost}</span> (pid{' '}
        {context.heldBy.holderPid}). Lease until{' '}
        <span className="text-white/80">{context.heldBy.leaseUntil}</span>.
      </p>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!context.isLive && (
          <button
            type="button"
            onClick={() => onDecision('take-over')}
            data-testid="interview-takeover-card-take-over"
            className="rounded-lg bg-amber-500/80 px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
          >
            Take over
          </button>
        )}
        <button
          type="button"
          onClick={() => onDecision('wait')}
          data-testid="interview-takeover-card-wait"
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 transition-colors hover:border-white/20 hover:text-white"
        >
          Wait
        </button>
        <button
          type="button"
          onClick={() => onDecision('start-over')}
          data-testid="interview-takeover-card-start-over"
          className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-200/90 transition-colors hover:bg-red-500/20"
        >
          Start over
        </button>
      </div>
    </div>
  );
}
